import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button, TextField } from '@/components/ui';
import { verifyOtpSchema, type VerifyOtpFormValues } from '../schemas/auth.schemas';
import { useResendOtp, useVerifyOtp } from '../hooks/useRegister';

interface OtpFormProps {
  email: string;
  onVerified: () => void;
}

export function OtpForm({ email, onVerified }: OtpFormProps) {
  const { t } = useTranslation();
  const verify = useVerifyOtp();
  const resend = useResendOtp();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<VerifyOtpFormValues>({
    resolver: zodResolver(verifyOtpSchema),
    defaultValues: { otp: '' },
  });

  const onSubmit = handleSubmit((values) => {
    verify.mutate({ email, otp: values.otp }, { onSuccess: onVerified });
  });

  const verifyError = verify.isError
    ? t(verify.error.message, { defaultValue: verify.error.message })
    : null;

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      <p className="text-sm text-ink-mute">{t('register.otp_sent', { email })}</p>

      {verifyError && (
        <div role="alert" className="rounded-input bg-danger-light px-3.5 py-2.5 text-sm text-danger">
          {verifyError}
        </div>
      )}
      {resend.isSuccess && (
        <div role="status" className="rounded-input bg-info-light px-3.5 py-2.5 text-sm text-info">
          {t('register.otp_resent')}
        </div>
      )}

      <TextField
        label={t('register.otp')}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="••••••"
        error={errors.otp ? t(errors.otp.message ?? '') : undefined}
        {...register('otp')}
      />

      <Button type="submit" variant="inverse" size="lg" block loading={verify.isPending}>
        {t('register.verify')}
      </Button>

      <button
        type="button"
        onClick={() => resend.mutate(email)}
        disabled={resend.isPending}
        className="text-center text-sm font-medium text-primary hover:underline disabled:opacity-50"
      >
        {t('register.resend')}
      </button>
    </form>
  );
}
